import { Icon } from '../components/Icon';
import type { IconName } from '../icons';
import {
  type TabId,
  type EditorForm,
  TAB_LABELS,
  tabFraction,
  stateOf,
} from './completion';
import type { TierResponse } from '../api/types';

const TAB_ICONS: Record<TabId, IconName> = {
  details: 'editSm',
  tools: 'tagSm',
  value: 'chartSm',
  bizcase: 'moneySm',
  improvements: 'growthSm',
};

interface TabBarProps {
  tabs: TabId[];
  active: TabId;
  form: EditorForm;
  tier: TierResponse;
  onSelect: (tab: TabId) => void;
}

/**
 * The 5-tab bar with per-tab progress bars. Green = done, amber = partial,
 * grey/transparent = not started (AC4.3). All tabs are freely clickable — no
 * sequential locking (AC4.2).
 */
export function TabBar({ tabs, active, form, onSelect }: TabBarProps) {
  return (
    <div className="flex border-b border-line" role="tablist">
      {tabs.map((tab, i) => {
        const fraction = tabFraction(tab, form);
        const state = stateOf(fraction);
        const isActive = tab === active;
        const labelColor =
          state === 'done' ? 'text-success' : isActive ? 'text-accent' : 'text-muted';
        const fillColor =
          state === 'done'
            ? 'bg-success'
            : state === 'partial'
              ? 'bg-warn'
              : 'bg-transparent';
        return (
          <button
            key={tab}
            role="tab"
            aria-selected={isActive}
            data-tab={tab}
            data-state={state}
            onClick={() => onSelect(tab)}
            className={`relative flex flex-1 flex-col items-center gap-1.5 pt-3.5 transition-colors hover:bg-panel ${
              i < tabs.length - 1 ? 'border-r border-line' : ''
            }`}
          >
            <div className="relative flex items-center gap-1.5 pb-2.5">
              <span className={state === 'done' ? 'text-success' : isActive ? 'text-accent' : 'text-muted'}>
                <Icon name={TAB_ICONS[tab]} size={14} />
              </span>
              <span className={`whitespace-nowrap text-[0.78rem] font-semibold ${labelColor}`}>
                {TAB_LABELS[tab]}
              </span>
              {state !== 'done' && (
                <span
                  data-testid={`attn-dot-${tab}`}
                  className={`absolute -right-2 -top-0.5 h-[7px] w-[7px] rounded-full border-[1.5px] border-white ${
                    state === 'partial' ? 'bg-warn' : 'bg-[#C0C9CA]'
                  }`}
                />
              )}
            </div>
            <div className="flex h-[3px] w-full">
              <div
                data-testid={`progress-${tab}`}
                className={`h-full transition-all ${fillColor}`}
                style={{ width: `${Math.round(fraction * 100)}%` }}
              />
            </div>
            {isActive && (
              <span
                className={`absolute bottom-0 left-0 right-0 h-0.5 ${
                  state === 'done' ? 'bg-success' : 'bg-accent'
                }`}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
